import {Component, Input} from '@angular/core';
import {formatDate} from '@angular/common';
import {Request} from '../../classes/request';
import {Customer} from '../../classes/customer';

type InvestmentCategory = 'Ordinario' | 'Green' | '4.0';
const KNOWN_INVESTMENT_CATEGORIES: InvestmentCategory[] = ['Ordinario', 'Green', '4.0'];

/**
 * Vista "Domanda Nuova Sabatini": replica la maschera della domanda con i dati
 * della pratica in sola lettura (copiabili uno per uno). I campi per cui la pratica
 * non contiene un dato corrispondente restano vuoti.
 */
@Component({
    selector: 'app-request-dns-view',
    templateUrl: './request-dns-view.component.html',
    styleUrls: ['./request-dns-view.component.css']
})
export class RequestDnsViewComponent {
    @Input() request: Request = new Request();

    readonly today = new Date();
    readonly country = 'ITALIA';
    private readonly amountFormatter = new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    get customer(): Customer | null {
        return this.request?.customer ?? null;
    }

    /** Codice ATECO: quello della pratica, in mancanza quello anagrafico del cliente. */
    get atecoCode(): string {
        return this.text(this.request?.AtecoCode) || this.text(this.customer?.AtecoCode);
    }

    get customerEmail(): string {
        return this.text(this.customer?.Email) || this.text(this.request?.Email);
    }

    get isBankFinancing(): boolean {
        return this.request?.InvestmentMode === 'Finanziamento';
    }

    get isLeasing(): boolean {
        return this.request?.InvestmentMode === 'Leasing';
    }

    get financingDuration(): string {
        const duration = this.request?.InvestmentDuration?.toString().trim();
        return duration ? `${duration} anni` : '';
    }

    /**
     * CF del firmatario: solo se il codice fiscale anagrafico è un CF persona
     * (16 caratteri), tipico delle ditte individuali. Il CF società a 11 cifre
     * non va copiato nel campo persona.
     */
    get signatoryFiscalCode(): string {
        const fiscalCode = this.text(this.customer?.FiscalCode).toUpperCase();
        return this.isPersonalFiscalCode(fiscalCode) ? fiscalCode : '';
    }

    /** Provincia italiana a 2 lettere => stato di nascita Italia. */
    get birthCountry(): string {
        const province = this.text(this.customer?.BirthPlaceProvince);
        return /^[A-Z]{2}$/i.test(province) ? this.country : '';
    }

    get contactRole(): string {
        return this.customer?.Name || this.customer?.Surname ? 'Legale rappresentante' : '';
    }

    get signaturePlace(): string {
        return this.text(this.customer?.HeadOffice);
    }

    get localUnitPhone(): string {
        return this.text(this.customer?.Phone);
    }

    /** Nessuna agevolazione richiesta/ottenuta -> prima opzione della dichiarazione. */
    get hasNoOtherConcessions(): boolean {
        return this.request?.Concessions === false;
    }

    get hasOtherConcessions(): boolean {
        return this.request?.Concessions === true;
    }

    /**
     * Impresa autonoma / associata-collegata, dedotta da "Società partecipata" e
     * "Soci partecipanti": se nessuno dei due è presente -> autonoma, altrimenti associata.
     */
    get isAutonomousCompany(): boolean {
        return this.request?.CompanyParticipation === false && this.request?.PartnersParticipation === false;
    }

    get isAssociatedCompany(): boolean {
        return this.request?.CompanyParticipation === true || this.request?.PartnersParticipation === true;
    }

    get investmentTotalDisplay(): string {
        return this.moneyDisplay(this.investmentAmount);
    }

    get investmentTotalCopy(): string {
        return this.moneyCopy(this.investmentAmount);
    }

    /**
     * Importo di una categoria di investimento (Ordinario = beni strumentali, 4.0, Green).
     * Se la pratica ha una sola categoria, l'intero importo le appartiene; se non la include vale 0;
     * se ne ha più d'una la ripartizione non è nota (null).
     */
    categoryAmount(category: InvestmentCategory): number | null {
        const amount = this.investmentAmount;
        const categories = this.investmentCategories;
        if (amount === null || categories.length === 0) {
            return null;
        }
        if (!categories.includes(category)) {
            return 0;
        }
        return categories.length === 1 ? amount : null;
    }

    categoryDisplay(category: InvestmentCategory): string {
        return this.moneyDisplay(this.categoryAmount(category));
    }

    categoryCopy(category: InvestmentCategory): string {
        return this.moneyCopy(this.categoryAmount(category));
    }

    street(address?: string | null, houseNumber?: string | null): string {
        return [address?.trim(), houseNumber?.trim()].filter(Boolean).join(' ');
    }

    date(value: Date | string | null | undefined): string {
        if (!value) {
            return '';
        }
        try {
            return formatDate(value, 'dd/MM/yyyy', 'en-US');
        } catch {
            return '';
        }
    }

    private get investmentAmount(): number | null {
        const raw = this.request?.InvestmentAmount;
        if (raw === null || raw === undefined || (raw as unknown) === '') {
            return null;
        }
        const parsed = Number(raw);
        return isNaN(parsed) ? null : parsed;
    }

    private get investmentCategories(): InvestmentCategory[] {
        const raw = this.request?.InvestmentType ?? '';
        if (!raw || raw === 'Nessuno') {
            return [];
        }
        return raw
            .split(',')
            .map(category => category.trim())
            .filter((category): category is InvestmentCategory =>
                KNOWN_INVESTMENT_CATEGORIES.includes(category as InvestmentCategory));
    }

    private moneyDisplay(amount: number | null): string {
        return amount === null ? '' : `${this.amountFormatter.format(amount)} €`;
    }

    /** Formato da incollare nei campi importo del portale: senza separatore migliaia né simbolo. */
    private moneyCopy(amount: number | null): string {
        return amount === null ? '' : amount.toFixed(2).replace('.', ',');
    }

    private text(value: string | number | null | undefined): string {
        if (value === null || value === undefined) {
            return '';
        }
        return String(value).trim();
    }

    private isPersonalFiscalCode(value: string): boolean {
        return /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/i.test(value);
    }
}
